import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import ClientService from "../../services/ClientService";
import SubscriptionService from "../../services/SubscriptionService";
import {
  SaPageShell,
  SaPageHeader,
  SaPrimaryButton,
  SaGhostButton,
  SaCard,
  SA,
} from "../shared/SuperAdminUi";

const GRADIENTS = ["#5e72e4", "#2dce89", "#fb6340", "#f5365c", "#11cdef", "#344767", "#825ee4", "#00875a"];
const pickColor = (str = "") => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return GRADIENTS[h % GRADIENTS.length];
};
const initials = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "?";

function subBadge(status) {
  const active = !!(status?.isActive ?? status?.IsActive ?? status?.hasActiveSubscription);
  const label = status?.planName ?? status?.PlanName ?? (active ? "Active" : "None");
  const color = active ? SA.success : SA.danger;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: `${color}18`, color }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: color }} />
      {label}
    </span>
  );
}

function Clients() {
  const navigate = useNavigate();
  const toast = useRef(null);
  const [clients, setClients] = useState([]);
  const [subMap, setSubMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(0);
  const pageSize = 15;

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const list = await ClientService.getAllCLients();
      setClients(list);
      const subs = await Promise.all(
        list.map(async (c) => {
          try {
            const st = await SubscriptionService.getClientSubscriptionStatus(c.id);
            return [c.id, st];
          } catch {
            return [c.id, null];
          }
        })
      );
      setSubMap(Object.fromEntries(subs));
    } catch {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to load clients.", life: 4000 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clients.filter((c) => {
      const sub = subMap[c.id];
      const active = !!(sub?.isActive ?? sub?.IsActive ?? sub?.hasActiveSubscription);
      if (filter === "active" && !active) return false;
      if (filter === "inactive" && active) return false;
      if (!q) return true;
      const hay = [c.agencyName, c.email, c.phoneNumber, c.city, c.state, c.country, c.gstNumber]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [clients, search, filter, subMap]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice(page * pageSize, page * pageSize + pageSize);

  useEffect(() => {
    setPage(0);
  }, [search, filter]);

  useEffect(() => {
    if (page >= pageCount) setPage(Math.max(0, pageCount - 1));
  }, [page, pageCount]);

  const stats = useMemo(() => {
    const activeSubs = clients.filter((c) => subMap[c.id]?.isActive ?? subMap[c.id]?.IsActive ?? subMap[c.id]?.hasActiveSubscription).length;
    return {
      total: clients.length,
      activeSubs,
      withGst: clients.filter((c) => c.gstNumber).length,
      countries: new Set(clients.map((c) => c.country).filter(Boolean)).size,
    };
  }, [clients, subMap]);

  return (
    <SaPageShell>
      <Toast ref={toast} />

      <SaPageHeader
        title="Agencies"
        subtitle="Search, filter, and manage all tenant clients. Open an agency to edit profile, users, integrations, and billing."
        actions={
          <>
            <SaGhostButton iconClass="fas fa-sync-alt" onClick={fetchClients} disabled={loading}>
              Refresh
            </SaGhostButton>
            <SaPrimaryButton iconClass="fas fa-plus" onClick={() => navigate("/addClient")}>
              Add agency
            </SaPrimaryButton>
          </>
        }
      />

      {!loading && clients.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 22 }}>
          {[
            { label: "Total agencies", value: stats.total, icon: "fa-building", color: SA.primary },
            { label: "Active subscription", value: stats.activeSubs, icon: "fa-check-circle", color: SA.success },
            { label: "With GST", value: stats.withGst, icon: "fa-file-invoice", color: "#11cdef" },
            { label: "Countries", value: stats.countries, icon: "fa-globe", color: "#fb6340" },
          ].map(({ label, value, icon, color }) => (
            <div
              key={label}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                background: "#fff",
                border: `1px solid ${SA.border}`,
                borderRadius: 12,
                padding: "12px 18px",
                boxShadow: "0 2px 8px rgba(52,71,103,0.06)",
                flex: "1 1 160px",
                minWidth: 140,
              }}
            >
              <div style={{ width: 36, height: 36, borderRadius: 9, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", color }}>
                <i className={`fas ${icon}`} />
              </div>
              <div>
                <p style={{ margin: 0, fontSize: 18, fontWeight: 800, color: SA.inkDeep, lineHeight: 1 }}>{value}</p>
                <p style={{ margin: 0, fontSize: 11, color: SA.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <SaCard noPadding>
        <div style={{ padding: "16px 20px", display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", borderBottom: `1px solid ${SA.border}` }}>
          <div style={{ flex: "1 1 220px", position: "relative" }}>
            <i className="fas fa-search" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: SA.muted, fontSize: 13 }} />
            <input
              type="search"
              placeholder="Search by name, email, phone, location…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px 10px 36px",
                borderRadius: 10,
                border: `1.5px solid ${SA.border}`,
                fontSize: 13,
                color: SA.ink,
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[
              ["all", "All"],
              ["active", "Subscribed"],
              ["inactive", "No subscription"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(key)}
                style={{
                  padding: "8px 14px",
                  borderRadius: 999,
                  border: filter === key ? `2px solid ${SA.primary}` : `1.5px solid ${SA.border}`,
                  background: filter === key ? `${SA.primary}12` : "#fff",
                  color: filter === key ? SA.primary : SA.muted,
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <span style={{ fontSize: 12, color: SA.muted, fontWeight: 600 }}>
            {filtered.length} of {clients.length}
          </span>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "72px 24px", color: SA.muted }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: 28, color: SA.primary, display: "block", marginBottom: 12 }} />
            Loading agencies…
          </div>
        ) : clients.length === 0 ? (
          <div style={{ textAlign: "center", padding: "64px 24px" }}>
            <i className="fas fa-building" style={{ fontSize: 40, color: `${SA.primary}40`, display: "block", marginBottom: 14 }} />
            <p style={{ margin: "0 0 18px", color: SA.muted }}>No agencies yet. Create your first client.</p>
            <SaPrimaryButton iconClass="fas fa-plus" onClick={() => navigate("/addClient")}>Add agency</SaPrimaryButton>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 24px", color: SA.muted }}>
            No agencies match your search or filter.
          </div>
        ) : (
          <>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: SA.gradientHdr }}>
                    {["Agency", "Contact", "Location", "Subscription", "Actions"].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: "12px 16px",
                          textAlign: "left",
                          fontSize: 10,
                          fontWeight: 800,
                          color: "rgba(255,255,255,0.75)",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((c) => {
                    const name = c.agencyName || "Unnamed";
                    const color = pickColor(name);
                    const loc = [c.city, c.state, c.country].filter(Boolean).join(", ");
                    return (
                      <tr
                        key={c.id}
                        style={{ borderBottom: `1px solid ${SA.border}`, transition: "background 0.12s" }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = SA.bgSoft; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                      >
                        <td style={{ padding: "12px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ width: 36, height: 36, borderRadius: 10, background: color, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 13, flexShrink: 0 }}>
                              {initials(name)}
                            </div>
                            <div>
                              <div style={{ fontWeight: 700, color: SA.ink }}>{name}</div>
                              <div style={{ fontSize: 11, color: SA.muted }}>ID {c.id}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: "12px 16px", color: SA.ink }}>
                          {c.email ? (
                            <a href={`mailto:${c.email}`} style={{ color: SA.primary, textDecoration: "none", fontWeight: 500 }}>{c.email}</a>
                          ) : (
                            <span style={{ color: SA.muted }}>—</span>
                          )}
                          {c.phoneNumber && (
                            <div style={{ fontSize: 12, color: SA.muted, marginTop: 2 }}>{c.phoneNumber}</div>
                          )}
                        </td>
                        <td style={{ padding: "12px 16px", color: SA.muted, maxWidth: 200 }}>
                          {loc || "—"}
                        </td>
                        <td style={{ padding: "12px 16px" }}>{subBadge(subMap[c.id])}</td>
                        <td style={{ padding: "12px 16px", whiteSpace: "nowrap" }}>
                          <SaGhostButton
                            type="button"
                            onClick={() => navigate(`/editClient/${c.id}`)}
                            style={{ padding: "7px 12px", fontSize: 12 }}
                          >
                            <i className="fas fa-cog" style={{ marginRight: 6 }} />
                            Manage
                          </SaGhostButton>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {pageCount > 1 && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", borderTop: `1px solid ${SA.border}` }}>
                <span style={{ fontSize: 12, color: SA.muted }}>
                  Page {page + 1} of {pageCount}
                </span>
                <div style={{ display: "flex", gap: 8 }}>
                  <SaGhostButton type="button" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</SaGhostButton>
                  <SaGhostButton type="button" disabled={page >= pageCount - 1} onClick={() => setPage((p) => p + 1)}>Next</SaGhostButton>
                </div>
              </div>
            )}
          </>
        )}
      </SaCard>
    </SaPageShell>
  );
}

export default Clients;
